import { ApolloError, useApolloClient } from "@apollo/client";
import { CheckboxFormik, IMultiSelectOptions, MultiSelectFormik, TextInputFormik } from "@starrez/starryui";
import { FormikProps } from "formik";
import * as React from "react";
import { ObjectSchema, SchemaDescription } from "yup";
import { ToggleButton } from "../../../components/Buttons/ToggleButton";
import { FormFieldSet, FormRow, FormRowInput, FormSubTitleSpan, FormTitleH2, FormWrapper } from "../../../components/Form";
import { IWizardFormProps } from "../../../components/Wizard";
import { WizardContext } from "../../../components/Wizard/Context/";
import { ErrorContext } from "../../../context";
import { AdvancedEmailWizardContext } from "../../../context/AdvancedEmailWizardContext";
import { IEntryAddressType, IFromAddress } from "../../../context/AdvancedEmailWizardContext/graphQL/types";
import { DynamicTextContextEnum } from "../../../enums/DynamicTextContext";
import { WizardTypeEnum } from "../../../enums/WizardTypeEnum";
import { strings } from "../../../localisation";
import * as EmailValidator from "../../../utils/EmailValidator";
import { IParticipantEmailDataModel } from "../../EmailWizard/models/ParticipantEmail";
import { ISendEmailDataModel } from "../../EmailWizard/models/SendEmail";
import { ParticipantDetails } from "../../EmailWizard/steps/ParticipantDetails/ParticipantDetails";
import { ITemplateDataModel } from "../../TemplateWizard/models/Template";
import { ScribanValidatorService } from "../../../data/graphQL/services/ScribanValidatorService";
import { ExecuteModel } from "../ExecuteModel";
import { IValidatedDynamicTextResult } from "./graphQL/validateDynamicText";
import { DynamicTextValidationErrors } from "./DynamicTextValidationErrors";

interface IRequiredMap {
    [key: string]: boolean;
}

const isRequiredField = <T extends ExecuteModel<ITemplateDataModel | ISendEmailDataModel | IParticipantEmailDataModel>>(ValidationSchema: ObjectSchema<object | undefined, object>, searchfield: string): boolean => {
    return ((ValidationSchema
        .describe()
        .fields[nameof.full<T>((m) => m.formDataModel)] as SchemaDescription)
        .fields[searchfield] as SchemaDescription)
        ?.tests
        .findIndex(({ name }: { name: string }) => name === "required") >= 0;
};

export interface IDetailsProps<T extends ExecuteModel<ITemplateDataModel | ISendEmailDataModel | IParticipantEmailDataModel>> extends IWizardFormProps<T> {
    ShowParticipants?: boolean;
}

export const Details = <T extends ExecuteModel<ITemplateDataModel | ISendEmailDataModel | IParticipantEmailDataModel>>({ FormikProps, FormikValues, IsShown, SetHasValidationErrors, ValidationSchema }
    : IDetailsProps<ExecuteModel<ITemplateDataModel | ISendEmailDataModel | IParticipantEmailDataModel>>) => {

    // Context
    const errorContext = React.useContext(ErrorContext);
    const advancedEmailWizardContext = React.useContext(AdvancedEmailWizardContext);
    const { AddressTypes, DynamicTextContext, WizardType } = advancedEmailWizardContext;

    const { IsCompleting } = React.useContext(WizardContext);

    // Services
    const apolloClient = useApolloClient();
    const scribanValidatorService = React.useMemo(() => new ScribanValidatorService(apolloClient, advancedEmailWizardContext), []);

    // State
    const [showCC, setShowCC] = React.useState(FormikProps?.values.formDataModel.EmailCC || false);
    const [showBCC, setShowBCC] = React.useState(FormikProps?.values.formDataModel.EmailBCC || false);
    const [requiredMap, setRequiredMap] = React.useState<IRequiredMap>({});
    const [dynamicTextValidationResult, setDynamicTextValidationResult] = React.useState<IValidatedDynamicTextResult[]>();
    const [showErrorModal, setShowErrorModal] = React.useState(false);

    // Set the required fields based on the YUP validation schema
    React.useEffect(() => {
        const map: { [key: string]: boolean } = {};

        for (const prop of Object.getOwnPropertyNames(FormikValues.formDataModel)) {
            map[prop] = isRequiredField<T>(ValidationSchema, prop);
        }

        setRequiredMap(map);
    }, [ValidationSchema, FormikValues.formDataModel]);

    // Build options
    let entryAddressOptions: IMultiSelectOptions[] = [];
    let fromAddressOptions: IMultiSelectOptions[] = [];

    if (AddressTypes) {
        entryAddressOptions = entryAddressOptions
            .concat(AddressTypes.entryAddressTypes
                .map((address: IEntryAddressType): IMultiSelectOptions => {
                    return { value: `${address.id}`, label: address.description };
                }));

        fromAddressOptions = fromAddressOptions
            .concat(AddressTypes.fromAddresses
                .map((address: IFromAddress): IMultiSelectOptions => {
                    return { value: `${address.id}`, label: address.description };
                }));
    }

    // Handle CC/BCC toggle buttons
    React.useEffect(() => {
        setShowCC(FormikProps?.values.formDataModel.EmailCC || false);
        setShowBCC(FormikProps?.values.formDataModel.EmailBCC || false);
    }, [FormikProps?.values.formDataModel.EmailCC, FormikProps?.values.formDataModel.EmailBCC]);

    const onCCToggle = (value: boolean) => {
        setShowCC(value);
        FormikProps?.setFieldValue(nameof.full<T>((m) => m.formDataModel.EmailCC), value);

        if (!value) {
            FormikProps?.setFieldValue(nameof.full<T>((m) => m.formDataModel.EmailCCAddressItems), []);
        }
    };

    const onBCCToggle = (value: boolean) => {
        setShowBCC(value);
        FormikProps?.setFieldValue(nameof.full<T>((m) => m.formDataModel.EmailBCC), value);

        if (!value) {
            FormikProps?.setFieldValue(nameof.full<T>((m) => m.formDataModel.EmailBCCAddressItems), []);
        }
    };

    /*
        The following effect is triggered when the Wizard is submitting on the current step.
        We make a call to the validation endpoint and do one of the following:
            1. If errors are returned from the request, a modal appears with the errors, fieldErrors are set on the
               email body to disable the submit button, HasValidationErrors callback returns with true to stop the
               wizard from progressing to the next step.
            2. If there are no errors returned, we set the email body and subject to the values returned from the response
               and set the HasValidationErrors callback to false which allows the wizard to progress to the next step
    */
    React.useEffect(() => {
        if (IsShown && FormikProps?.isSubmitting && FormikProps?.isValid && WizardType !== WizardTypeEnum.Template) {
            (async () => scribanValidatorService.ValidateEmailDetails(FormikProps?.values)
                .then((response) => {

                    FormikProps?.setSubmitting(false);

                    setDynamicTextErrors(response);

                }).catch((error: ApolloError) => {
                    errorContext.SetError(error);
                })
            )();
        }
    }, [FormikProps?.isSubmitting]);

    const setDynamicTextErrors = (response: IValidatedDynamicTextResult[]) => {

        if (response?.some((result) => result.errors.length)) {
            setDynamicTextValidationResult(response);
            setShowErrorModal(true);

            SetHasValidationErrors!(true);
        } else {
            setShowErrorModal(false);

            SetHasValidationErrors!(false);
        }
    };

    const participantFormikProps = FormikProps as FormikProps<ExecuteModel<IParticipantEmailDataModel>>;

    return (
        <>
            { showErrorModal && dynamicTextValidationResult ?
                <DynamicTextValidationErrors Errors={dynamicTextValidationResult} OnClose={() => setShowErrorModal(false)} />
                : null
            }

            <FormFieldSet disabled={IsCompleting}>
                <FormWrapper SpaceBefore>

                    {[DynamicTextContextEnum.ConcernEntry, DynamicTextContextEnum.IncidentEntry, DynamicTextContextEnum.ContributionEntry].includes(DynamicTextContext) &&
                        participantFormikProps &&
                        <ParticipantDetails
                            IDs={FormikValues.ids!}
                            DynamicTextContext={DynamicTextContext}
                            WizardType={WizardType}
                            FormikProps={participantFormikProps!}
                            Shown={IsShown}
                        />
                    }

                    {DynamicTextContext === DynamicTextContextEnum.Template && (
                        <>
                            <FormTitleH2>
                                {strings.Label_Step_TemplateDetails_Title}
                                <FormSubTitleSpan>
                                    {strings.Label_Step_TemplateDetails_SubTitle}
                                </FormSubTitleSpan>
                            </FormTitleH2>

                            <TextInputFormik
                                FormikProps={FormikProps}
                                Name={nameof.full<ExecuteModel<ITemplateDataModel>>((m) => m.formDataModel.TemplateName)}
                                Label={strings.Label_Template_Name}
                                ShowRequired={requiredMap.TemplateName}
                                MaxLength={50}
                                Autocomplete="template-name"
                            />
                        </>
                    )}
                    <TextInputFormik
                        FormikProps={FormikProps}
                        Name={nameof.full<T>((m) => m.formDataModel.CorrespondenceName)}
                        Label={strings.Label_Email_Name}
                        HelperText={strings.Label_Email_Name_HelperText}
                        ShowRequired={requiredMap.CorrespondenceName}
                        MaxLength={100}
                        Autocomplete="email-name"
                    />

                    <TextInputFormik
                        FormikProps={FormikProps}
                        Name={nameof.full<T>((m) => m.formDataModel.EmailSubject)}
                        Label={strings.Label_Email_Subject}
                        ShowRequired={requiredMap.EmailSubject}
                        MaxLength={90}
                        Autocomplete="email-subject"
                    />

                    <MultiSelectFormik
                        FormikProps={FormikProps}
                        Name={nameof.full<T>((m) => m.formDataModel.EmailToItems)}
                        Label={strings.Label_Email_RecipientAddress}
                        HelperText={strings.Label_Email_RecipientAddress_HelperText}
                        Options={entryAddressOptions}
                        ShowRequired={requiredMap.EmailToItems}
                        IsMultiSelect={true}
                        AllowFreeText={true}
                        OnValidateFreeText={EmailValidator.validateEmailAddress}
                        Autocomplete="none"
                    />

                    <FormRow>
                        <ToggleButton
                            UncheckedCaption={strings.Action_Email_CCAdd}
                            CheckedCaption={strings.Action_Email_CCRemove}
                            OnClick={onCCToggle}
                            Toggled={showCC}
                        />
                        <ToggleButton
                            UncheckedCaption={strings.Action_Email_BCCAdd}
                            CheckedCaption={strings.Action_Email_BCCRemove}
                            OnClick={onBCCToggle}
                            Toggled={showBCC}
                        />
                    </FormRow>

                    {showCC &&
                        <FormRow NoMargin={true}>
                            <FormRowInput>
                                <MultiSelectFormik
                                    FormikProps={FormikProps}
                                    Name={nameof.full<T>((m) => m.formDataModel.EmailCCAddressItems)}
                                    Label={strings.Label_Email_CC}
                                    Options={entryAddressOptions}
                                    ShowRequired={FormikProps && FormikProps.values.formDataModel.EmailCC}
                                    IsMultiSelect={true}
                                    AllowFreeText={true}
                                    OnValidateFreeText={EmailValidator.validateEmailAddress}
                                    Autocomplete="none"
                                />
                            </FormRowInput>
                            <CheckboxFormik
                                FormikProps={FormikProps}
                                Name={nameof.full<T>((m) => m.formDataModel.CCAddressOnlyFirst)}
                                Label={strings.Label_Email_FirstOnly}
                            />

                        </FormRow>
                    }

                    {showBCC &&
                        <FormRow NoMargin={true}>
                            <FormRowInput>
                                <MultiSelectFormik
                                    FormikProps={FormikProps}
                                    Name={nameof.full<T>((m) => m.formDataModel.EmailBCCAddressItems)}
                                    Label={strings.Label_Email_BCC}
                                    Options={entryAddressOptions}
                                    ShowRequired={FormikProps && FormikProps.values.formDataModel.EmailBCC}
                                    IsMultiSelect={true}
                                    AllowFreeText={true}
                                    OnValidateFreeText={EmailValidator.validateEmailAddress}
                                    Autocomplete="none"
                                />
                            </FormRowInput>
                            <CheckboxFormik
                                FormikProps={FormikProps}
                                Name={nameof.full<T>((m) => m.formDataModel.BCCAddressOnlyFirst)}
                                Label={strings.Label_Email_FirstOnly}
                            />

                        </FormRow>
                    }

                    <MultiSelectFormik
                        FormikProps={FormikProps}
                        Name={nameof.full<T>((m) => m.formDataModel.EmailFromItem)}
                        Label={strings.Label_Email_From}
                        Options={fromAddressOptions}
                        ShowRequired={requiredMap.EmailFromItem}
                        IsMultiSelect={false}
                        CloseMenuOnSelect={true}
                        Autocomplete="none"
                    />

                    <CheckboxFormik
                        FormikProps={FormikProps}
                        Name={nameof.full<T>((m) => m.formDataModel.ViewOnWeb)}
                        Label={strings.Label_Email_ShowInPortal}
                    />

                </FormWrapper>
            </FormFieldSet>
        </>
    );
};
